// Градове › form — one city row, add or edit. The centre and radius drive the map and the hex
// sweep; the Latin name and slug drive the Latin queries and the directory URLs.

import { useState } from 'react';
import { useSaveCity, type CityRow } from './useCities.js';
import { LinesField, NumberField, TextField, ToggleField } from '../verticals/fields.js';

export const EMPTY_CITY: CityRow = {
  key: '',
  name_bg: '',
  name_en: '',
  name_lat: '',
  slug: '',
  aliases: [],
  center_lat: null,
  center_lng: null,
  radius_m: 8000,
  active: false,
};

export function CityForm({
  city,
  isNew,
  onDone,
}: {
  city: CityRow;
  isNew: boolean;
  onDone: () => void;
}) {
  const save = useSaveCity();
  const [draft, setDraft] = useState<CityRow>(city);
  const set = <K extends keyof CityRow>(field: K, value: CityRow[K]) =>
    setDraft((prev) => ({ ...prev, [field]: value }));

  const keyOk = /^[a-z][a-z0-9_]{1,40}$/.test(draft.key);
  const canSave = keyOk && draft.name_bg.trim() !== '' && draft.name_en.trim() !== '';

  return (
    <form
      className="adm__editor"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate(
          {
            city: {
              ...draft,
              name_lat: draft.name_lat || draft.name_en,
              slug: draft.slug || draft.key,
            },
            isNew,
          },
          { onSuccess: onDone },
        );
      }}
    >
      <div className="adm__editor-head">
        <h3 className="adm__h">{isNew ? 'Нов град' : `${city.name_bg} — редакция`}</h3>
        <div className="adm__actions">
          <button type="submit" className="adm__resolve-btn" disabled={!canSave || save.isPending}>
            {save.isPending ? 'Записва…' : 'Запази'}
          </button>
          <button type="button" className="adm__json-btn" onClick={onDone}>
            Затвори
          </button>
        </div>
      </div>
      {save.isError ? <p className="adm__err">{(save.error as Error).message}</p> : null}

      <div className="adm__form adm__form--cols">
        <fieldset className="adm__fieldset">
          <legend className="adm__legend">Имена</legend>
          {isNew ? (
            <TextField
              label="Ключ"
              value={draft.key}
              onChange={(v) => set('key', v.trim())}
              hint="малки латински букви: varna, stara_zagora — не се променя после"
              mono
            />
          ) : null}
          <TextField label="Име (бг)" value={draft.name_bg} onChange={(v) => set('name_bg', v)} />
          <TextField label="Име (en)" value={draft.name_en} onChange={(v) => set('name_en', v)} />
          <TextField
            label="Име на латиница"
            value={draft.name_lat ?? ''}
            onChange={(v) => set('name_lat', v)}
            hint="Както хората го пишат в Google: Burgas. Празно = английското име."
          />
          <TextField
            label="Slug"
            value={draft.slug ?? ''}
            onChange={(v) => set('slug', v)}
            hint="За адресите на директориите (superdoc.bg/…/burgas). Празно = ключът."
            mono
          />
          <LinesField
            label="Други изписвания (aliases)"
            value={draft.aliases}
            onChange={(v) => set('aliases', v)}
            hint="Бургас, гр. Бургас, Burgas — за разпознаване в адреси."
            rows={3}
          />
        </fieldset>
        <fieldset className="adm__fieldset">
          <legend className="adm__legend">Територия</legend>
          <NumberField
            label="Център — ширина (lat)"
            value={draft.center_lat}
            onChange={(v) => set('center_lat', v)}
          />
          <NumberField
            label="Център — дължина (lng)"
            value={draft.center_lng}
            onChange={(v) => set('center_lng', v)}
          />
          <NumberField
            label="Радиус (м)"
            value={draft.radius_m}
            onChange={(v) => set('radius_m', v)}
            min={500}
            hint="Докъде стига мрежовото претърсване; 8 000 за областен град."
          />
          <ToggleField
            label="Активен град"
            checked={draft.active}
            onChange={(v) => set('active', v)}
            hint="Картата се центрира върху първия активен град."
          />
        </fieldset>
      </div>
    </form>
  );
}
